import { getColumnCount, padRow, splitReferentialGrid } from './referentialGrid'

export const ROW_META_COLUMN_HEADER = '__row_meta__'

export type ReferentialRowUpdateMeta = {
  updatedAt: string
  updatedBy: string
}

export function isReservedGridColumnHeader(header: string): boolean {
  const normalized = header.trim()
  return normalized.startsWith('__') && normalized.endsWith('__')
}

export function serializeRowMetaCell(meta?: ReferentialRowUpdateMeta | null): string {
  if (!meta?.updatedAt || !meta.updatedBy) {
    return ''
  }

  return JSON.stringify({
    updatedAt: meta.updatedAt,
    updatedBy: meta.updatedBy,
  })
}

export function parseRowMetaCell(cell: string): ReferentialRowUpdateMeta | null {
  const trimmed = cell.trim()

  if (!trimmed) {
    return null
  }

  try {
    const parsed = JSON.parse(trimmed) as {
      updatedAt?: unknown
      updatedBy?: unknown
    }

    if (
      typeof parsed.updatedAt !== 'string' ||
      typeof parsed.updatedBy !== 'string' ||
      !parsed.updatedAt ||
      !parsed.updatedBy
    ) {
      return null
    }

    return {
      updatedAt: parsed.updatedAt,
      updatedBy: parsed.updatedBy,
    }
  } catch {
    return null
  }
}

export function getRowMetaColumnIndex(headers: string[]): number | null {
  if (!headers.length) {
    return null
  }

  const lastIndex = headers.length - 1

  if (headers[lastIndex]?.trim() === ROW_META_COLUMN_HEADER) {
    return lastIndex
  }

  return null
}

function mergeGridRows(headers: string[], rows: string[][]): string[][] {
  if (!headers.length && !rows.length) {
    return [['']]
  }

  if (!rows.length) {
    return [headers.length ? [...headers] : ['']]
  }

  return [headers, ...rows.map((row) => [...row])]
}

export function stripReservedColumnsFromGrid(gridData: string[][]): string[][] {
  if (!gridData.length) {
    return gridData
  }

  const { headers, rows } = splitReferentialGrid(gridData)
  const reservedIndices = new Set(
    headers
      .map((header, index) => (isReservedGridColumnHeader(header) ? index : -1))
      .filter((index) => index >= 0)
  )

  if (!reservedIndices.size) {
    return gridData
  }

  const nextHeaders = headers.filter((_, index) => !reservedIndices.has(index))
  const nextRows = rows.map((row) => row.filter((_, index) => !reservedIndices.has(index)))

  return mergeGridRows(nextHeaders, nextRows)
}

export function attachRowMetaColumn(
  gridData: string[][],
  rowUpdatedAt: Record<number, ReferentialRowUpdateMeta>
): string[][] {
  const businessGrid = stripReservedColumnsFromGrid(gridData)
  const { headers, rows } = splitReferentialGrid(businessGrid)
  const columnCount = getColumnCount(headers, rows)
  const nextHeaders = [...padRow(headers, columnCount), ROW_META_COLUMN_HEADER]

  if (!rows.length) {
    return [nextHeaders]
  }

  const nextRows = rows.map((row, index) => [
    ...padRow(row, columnCount),
    serializeRowMetaCell(rowUpdatedAt[index]),
  ])

  return mergeGridRows(nextHeaders, nextRows)
}

export function detachRowMetaColumn(gridData: string[][]): {
  gridData: string[][]
  rowUpdatedAt: Record<number, ReferentialRowUpdateMeta>
} {
  if (!gridData.length) {
    return { gridData: [['']], rowUpdatedAt: {} }
  }

  const headers = gridData[0] ?? []
  const metaIndex = getRowMetaColumnIndex(headers)

  if (metaIndex == null) {
    return {
      gridData: stripReservedColumnsFromGrid(gridData),
      rowUpdatedAt: {},
    }
  }

  const rowUpdatedAt: Record<number, ReferentialRowUpdateMeta> = {}
  const nextHeaders = headers.slice(0, metaIndex)
  const nextRows = gridData.slice(1).map((row, rowIndex) => {
    const meta = parseRowMetaCell(row[metaIndex] ?? '')

    if (meta) {
      rowUpdatedAt[rowIndex] = meta
    }

    return row.slice(0, metaIndex)
  })

  return {
    gridData: mergeGridRows(nextHeaders, nextRows),
    rowUpdatedAt,
  }
}

export function isReservedColumnIndex(headers: string[], columnIndex: number): boolean {
  return isReservedGridColumnHeader(headers[columnIndex] ?? '')
}
